import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertOctagon,
  Archive,
  Banknote,
  BarChart3,
  Building2,
  Boxes,
  Calculator,
  CalendarRange,
  ClipboardCheck,
  Coins,
  Database,
  FileBarChart,
  FileCheck,
  FolderArchive,
  KeyRound,
  LayoutDashboard,
  Package,
  Scale,
  Send,
  Settings,
  ShieldCheck,
  Truck,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useBatchContext } from '@/app/BatchContext';
import { useCurrentUser } from '@/lib/auth-context';
import { canAccessPath } from '@/lib/role-access';
import { resolvePerBatchPath } from '@/lib/batch-routes';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';

interface NavItem {
  to: string;
  icon: LucideIcon;
  i18nKey: string;
  i18nNs?: string;
  feature?: 'file-library';
}

interface NavGroup {
  i18nKey: string;
  items: NavItem[];
}

/**
 * Sidebar groups mirror the EFS layout in
 * docs/specs/08-pa-jp-design-reference.md §5. The first-level groups also
 * map to the route prefixes in `docs/specs/06-ui-api-spec.md` §2.
 *
 * Operations sub-routes are not directly reachable as plain top-level
 * routes — they live under `/batches/:batchId/...`. The sidebar shows
 * them as a *flat* shortcut group; the active batch context is resolved
 * by the page itself.
 */
const GROUPS: NavGroup[] = [
  {
    i18nKey: 'efs.common.nav.dashboard',
    items: [
      { to: '/', icon: LayoutDashboard, i18nKey: 'efs.common.nav.dashboard' },
      { to: '/batches', icon: CalendarRange, i18nKey: 'efs.common.nav.monthly_batches' },
    ],
  },
  {
    i18nKey: 'efs.common.nav.dispatch_section',
    items: [
      { to: '/dispatch', icon: Send, i18nKey: 'efs.common.nav.dispatch' },
      { to: '/supplier/batches', icon: CalendarRange, i18nKey: 'efs.common.nav.supplier_portal' },
    ],
  },
  {
    i18nKey: 'efs.common.nav.operations',
    items: [
      { to: '/operations/inbound', icon: Truck, i18nKey: 'efs.common.nav.inbound' },
      { to: '/operations/sorting', icon: Workflow, i18nKey: 'efs.common.nav.sorting' },
      { to: '/operations/dismantling', icon: Wrench, i18nKey: 'efs.common.nav.dismantling' },
      { to: '/operations/inventory', icon: Boxes, i18nKey: 'efs.common.nav.inventory' },
      {
        to: '/operations/outbounds',
        icon: Package,
        i18nKey: 'efs.common.nav.outbounds',
      },
      {
        to: '/operations/weight-difference',
        icon: Scale,
        i18nKey: 'efs.common.nav.weight_difference',
      },
    ],
  },
  {
    i18nKey: 'efs.common.nav.masters',
    items: [
      {
        to: '/masters/incoming-items',
        icon: Database,
        i18nKey: 'efs.common.nav.incoming_items',
      },
      { to: '/masters/materials', icon: Database, i18nKey: 'efs.common.nav.materials' },
      // Name-normalization is alias management folded into the incoming-item
      // editor (別名 panel) — no standalone nav entry.
      { to: '/masters/destinations', icon: Truck, i18nKey: 'efs.common.nav.destinations' },
      { to: '/masters/package-types', icon: Boxes, i18nKey: 'efs.common.nav.package_types' },
      {
        to: '/masters/dismantling-methods',
        icon: Wrench,
        i18nKey: 'efs.common.nav.dismantling_methods',
      },
      { to: '/masters/warehouses', icon: Archive, i18nKey: 'efs.nav.masters.warehouses' },
      { to: '/masters/suppliers', icon: Truck, i18nKey: 'efs.nav.masters.suppliers' },
    ],
  },
  {
    i18nKey: 'efs.common.nav.pricing',
    items: [
      { to: '/pricing/references', icon: Coins, i18nKey: 'efs.common.nav.price_references' },
      { to: '/pricing/ups-scenarios', icon: BarChart3, i18nKey: 'efs.common.nav.ups_scenarios' },
    ],
  },
  {
    i18nKey: 'efs.common.nav.valuation_section',
    items: [
      { to: '/valuation/first', icon: Calculator, i18nKey: 'efs.common.nav.first_valuation' },
      {
        to: '/valuation/monthly-material',
        icon: Scale,
        i18nKey: 'efs.common.nav.monthly_material_valuation',
      },
      {
        to: '/valuation/supplier-purchase-price',
        icon: Coins,
        i18nKey: 'efs.common.nav.getit_purchase_price',
      },
      { to: '/settlement', icon: Banknote, i18nKey: 'efs.common.nav.ef_dk_settlement' },
      { to: '/reports/monthly', icon: FileBarChart, i18nKey: 'efs.common.nav.monthly_reports' },
      {
        to: '/valuation/supplier-quotation',
        icon: Coins,
        i18nKey: 'efs.common.nav.supplier_quotation',
      },
      {
        to: '/operations/buyer-proposals',
        icon: ClipboardCheck,
        i18nKey: 'efs.common.nav.buyer_proposals',
      },
    ],
  },
  {
    i18nKey: 'efs.common.nav.evidence',
    items: [
      {
        to: '/evidence/library',
        icon: FolderArchive,
        i18nKey: 'efs.file_library.nav.title',
        i18nNs: 'fileLibrary',
        feature: 'file-library',
      },
      { to: '/evidence', icon: Archive, i18nKey: 'efs.common.nav.evidence' },
      { to: '/approvals', icon: ClipboardCheck, i18nKey: 'efs.common.nav.approvals' },
      { to: '/exceptions', icon: AlertOctagon, i18nKey: 'efs.common.nav.exceptions' },
      { to: '/mass-balance', icon: BarChart3, i18nKey: 'efs.common.nav.mass_balance' },
      { to: '/audit-package', icon: FileCheck, i18nKey: 'efs.common.nav.audit_package' },
      { to: '/corrections', icon: ClipboardCheck, i18nKey: 'efs.common.nav.corrections' },
    ],
  },
  {
    i18nKey: 'efs.common.nav.settings',
    items: [
      // Every signed-in account: no permission code gates /settings/my-account.
      { to: '/settings/my-account', icon: KeyRound, i18nKey: 'efs.common.nav.my_account' },
      { to: '/settings/users', icon: Settings, i18nKey: 'efs.common.nav.users' },
      { to: '/settings/roles', icon: ShieldCheck, i18nKey: 'efs.common.nav.roles' },
      { to: '/settings/work-packages', icon: Boxes, i18nKey: 'efs.common.nav.work_packages' },
      { to: '/settings/companies', icon: Building2, i18nKey: 'efs.common.nav.companies' },
      { to: '/settings/facilities', icon: Building2, i18nKey: 'efs.common.nav.facilities' },
    ],
  },
];

interface SidebarProps {
  /** When set, called after the user clicks a nav link. Used by the mobile
   * drawer to auto-close on navigation. */
  onNavigate?: () => void;
  className?: string;
}

export const Sidebar = ({ onNavigate, className }: SidebarProps = {}) => {
  const { t } = useTranslation();
  const { batchId } = useBatchContext();
  const user = useCurrentUser();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();
  const resolveTo = (to: string): string => resolvePerBatchPath(to, batchId);
  // Drop nav items the user can't access; drop groups that go empty.
  // Matches BE permission gates so users never see links they'd be 403'd on.
  const visibleGroups = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.feature === 'file-library' && !fileLibraryEnabled) return false;
      return canAccessPath(user, item.to);
    }),
  })).filter((group) => group.items.length > 0);
  return (
    <aside
      className={cn(
        'flex h-full w-60 shrink-0 flex-col border-r border-efs-border bg-efs-bg-panel',
        className,
      )}
      aria-label="Primary"
    >
      <div className="flex h-12 items-center gap-2 border-b border-efs-border px-4">
        <div className="h-5 w-5 rounded-sm bg-efs-accent" aria-hidden />
        <div className="leading-tight">
          <div className="text-sm font-semibold text-efs-text-primary">
            {t('efs.common.app.name')}
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {visibleGroups.map((group) => (
          <div key={group.i18nKey} className="mb-4 last:mb-0">
            <div className="px-2 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-efs-text-muted">
              {t(group.i18nKey)}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={resolveTo(item.to)}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
                        'text-efs-text-primary hover:bg-efs-bg-subtle',
                        isActive && 'bg-efs-bg-subtle text-efs-accent font-medium',
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{t(item.i18nKey, { ns: item.i18nNs })}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-efs-border px-4 py-2 text-[0.6875rem] text-efs-text-muted">
        {t('efs.common.app.tagline')}
      </div>
    </aside>
  );
};
